import type { useGT } from 'gt-react';
import type {
  ApExceptionRuleSetSummary,
  PreviewedInvoiceExceptions,
  PreviewAPInvoiceExceptionsResponse,
  SuggestedExceptionTestInvoice,
} from '@erp/rpc';
import type { SelectDropdownOption } from '@/components/common/SelectDropdown';

type Translate = ReturnType<typeof useGT>;

export type RuleSetOptionLabelParams = {
  name: string;
  enumName: string;
  ruleCount: number;
};

export function ruleSetOptionLabel(
  gt: Translate,
  params: RuleSetOptionLabelParams,
): string {
  const vars = {
    context: 'exception rule set picker option label',
    name: params.name,
    enumName: params.enumName,
    count: String(params.ruleCount),
  };
  if (params.ruleCount === 1) {
    return gt('{name} — {enumName} ({count} rule)', vars);
  }
  return gt('{name} — {enumName} ({count} rules)', vars);
}

/** Dropdown options for the rule set picker in the test dialog. */
export function ruleSetOptions(
  ruleSets: readonly ApExceptionRuleSetSummary[],
  formatLabel: (params: RuleSetOptionLabelParams) => string,
): SelectDropdownOption[] {
  return ruleSets.map((ruleSet) => ({
    id: ruleSet.id,
    label: ruleSet.exceptionEnumName
      ? formatLabel({
          name: ruleSet.name,
          enumName: ruleSet.exceptionEnumName,
          ruleCount: ruleSet.ruleCount,
        })
      : ruleSet.name,
    display: ruleSet.name,
  }));
}

export type RuleSetPickerState = {
  placeholderKey: 'loading' | 'select';
  disabled: boolean;
  canRun: boolean;
  showEmptyHint: boolean;
};

/** Use the only rule set when the org has exactly one. */
export function resolveSelectedRuleSetId(
  selectedId: string | null,
  optionIds: readonly string[],
): string | null {
  if (selectedId && optionIds.includes(selectedId)) return selectedId;
  if (optionIds.length === 1) return optionIds[0] ?? null;
  return null;
}

/** Picker + Run button state from the loading / selection / running flags. */
export function ruleSetPickerState(params: {
  isLoading: boolean;
  isRunning: boolean;
  optionCount: number;
  selectedId: string | null;
}): RuleSetPickerState {
  const { isLoading, isRunning, optionCount, selectedId } = params;
  return {
    placeholderKey: isLoading ? 'loading' : 'select',
    disabled: isLoading || isRunning,
    canRun: selectedId !== null && !isRunning,
    showEmptyHint: !isLoading && optionCount === 0,
  };
}

export function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export type PreviewVerdict = 'clear' | 'blocked-owned' | 'blocked-unassigned';

/**
 * One-line outcome of a preview: nothing fired, fired with an owner, or
 * fired but no rule routes any of the raised types.
 */
export function previewVerdict(
  result: Pick<PreviewedInvoiceExceptions, 'raised' | 'owner'>,
): PreviewVerdict {
  if (result.raised.length === 0) return 'clear';
  return result.owner ? 'blocked-owned' : 'blocked-unassigned';
}

/** Raised types the set has no rule for — they would open unassigned. */
export function unroutedRaised(
  result: Pick<PreviewedInvoiceExceptions, 'raised'>,
): PreviewedInvoiceExceptions['raised'] {
  return result.raised.filter((exception) => !exception.hasRule);
}

/** Whether the dry-run actually called generateText. */
export function evaluatorCallState(
  result: Pick<PreviewedInvoiceExceptions, 'llmCalled'>,
): 'called' | 'skipped-empty-vocabulary' {
  return result.llmCalled ? 'called' : 'skipped-empty-vocabulary';
}

export function firstPreviewResult(
  response: PreviewAPInvoiceExceptionsResponse | undefined,
): PreviewedInvoiceExceptions | undefined {
  return response?.results[0];
}

export function previewByInvoiceId(
  response: PreviewAPInvoiceExceptionsResponse | undefined,
): Map<string, PreviewedInvoiceExceptions> {
  return new Map((response?.results ?? []).map((row) => [row.invoiceId, row]));
}

export type InvoiceBenchVerdict =
  'pending' | 'clear' | 'blocked-owned' | 'blocked-unassigned';

export type InvoiceBenchCardState = {
  displayNumber: string;
  supplierName: string;
  status: string;
  totalAmount: string;
  currency: string;
  whySelected: SuggestedExceptionTestInvoice['whySelected'];
  matching: SuggestedExceptionTestInvoice['matching'];
  verdict: InvoiceBenchVerdict;
  ownerTypeName: string | null;
  ownerDisplay: string | null;
  raised: PreviewedInvoiceExceptions['raised'];
  factsText: string;
};

/** Display fields for one curated invoice on the rule-set test bench. */
export function invoiceBenchCardState(
  invoice: SuggestedExceptionTestInvoice,
  result: PreviewedInvoiceExceptions | undefined,
  unknownSupplier: string,
): InvoiceBenchCardState {
  const owner = result?.owner ?? null;
  return {
    displayNumber: invoice.invoiceNumber ?? invoice.invoiceId.slice(0, 8),
    supplierName: invoice.supplierName ?? unknownSupplier,
    status: invoice.status,
    totalAmount: invoice.totalAmount ?? '?',
    currency: invoice.currency,
    whySelected: invoice.whySelected,
    matching: invoice.matching,
    verdict: result ? previewVerdict(result) : 'pending',
    ownerTypeName: owner?.exceptionTypeName ?? null,
    ownerDisplay: owner ? (owner.name ?? owner.id) : null,
    raised: result?.raised ?? [],
    factsText: invoice.factsText,
  };
}
